import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import classNames from 'classnames';
import { getInputSizeStyles, getInputStateClass } from './utils';
import { UXPContext } from "@core/context/Context";
import Icon from "@icon/Icon";
import { Dropdown } from '@widget/popups/Dropdown';
import DropdownIndicator from '@widget/other/DropdownIndicator';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import { getFontAwesomeIcon, isFontawesomeIcon } from '@utils';
import SearchBox from './SearchBox';
import { fillFixed, maskRest, parseMask, strictValue } from './input-mask';
/** @internal */
function isAddonDropdown(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v) && 'options' in v;
}
/** @internal */
function isPrefixConfig(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v) &&
        'value' in v && !('options' in v);
}
/** @internal — render a FontAwesome string or image URL as an icon node */
function renderAddonIcon(icon) {
    if (!icon)
        return null;
    if (isFontawesomeIcon(icon)) {
        return (_jsx("div", { className: "fa-icon", children: _jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(icon) }) }));
    }
    return (_jsx("div", { className: "image-icon", children: _jsx("div", { className: "icon", style: { backgroundImage: `url(${icon})` } }) }));
}
const AddonDropdownContent = ({ config, close, onSelect }) => {
    var _a;
    const [query, setQuery] = React.useState('');
    const [selIndex, setSelIndex] = React.useState(-1);
    const listRef = React.useRef(null);
    const shouldShowSearch = (_a = config.showSearch) !== null && _a !== void 0 ? _a : config.options.length > 5;
    const filtered = query.trim()
        ? config.options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
        : config.options;
    const hasIcons = config.options.some(o => !!o.icon);
    // Reset highlight when filter changes
    React.useEffect(() => { setSelIndex(-1); }, [query]);
    // Scroll highlighted option into view
    React.useEffect(() => {
        if (selIndex < 0 || !listRef.current)
            return;
        const item = listRef.current.children[selIndex];
        if (item)
            item.scrollIntoView({ block: 'nearest' });
    }, [selIndex]);
    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelIndex(prev => Math.min(prev + 1, filtered.length - 1));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelIndex(prev => Math.max(prev - 1, 0));
        }
        else if (e.key === 'Enter' && selIndex >= 0 && filtered[selIndex]) {
            e.preventDefault();
            onSelect(filtered[selIndex].value);
            close();
        }
        else if (e.key === 'Escape') {
            e.stopPropagation();
            close();
        }
    };
    return (_jsxs("div", { onKeyDown: handleKeyDown, children: [shouldShowSearch && (_jsx(SearchBox, { value: query, onChange: setQuery, className: "uxpcore_select__search", autoFocus: true })), _jsxs("div", { className: "uxpcore_select__dropdown-list", ref: listRef, children: [filtered.map((opt, i) => (_jsx("div", { className: classNames('uxpcore_select__option', {
                            'uxpcore_select__option--selected': opt.value === config.selected,
                            'uxpcore_select__option--highlighted': i === selIndex,
                        }), onMouseDown: (e) => {
                            e.preventDefault();
                            onSelect(opt.value);
                            close();
                        }, onMouseEnter: () => setSelIndex(i), children: _jsxs("div", { className: classNames('uxpcore_select__option-content', {
                                'uxpcore_select__option-content--has-icon': hasIcons,
                            }), children: [hasIcons && (_jsx("div", { className: "uxpcore_select__option-icon", children: renderAddonIcon(opt.icon) })), _jsx("div", { className: "uxpcore_select__option-label", children: opt.label })] }) }, opt.value))), filtered.length === 0 && (_jsx("div", { className: "uxpcore_select__no-options", children: "No options found" }))] })] }));
};
/**
 * A standard text input.
 * @export
 *
 * @category Inputs/Text
 */
const Input = React.forwardRef((props, ref) => {
    var _a, _b, _c, _d, _f, _g, _h, _j, _l, _m;
    const context = React.useContext(UXPContext);
    const { type = 'text', value, onChange, className, prefix, suffix, isValid, inputAttr, placeholder, inline, style, readOnly, disabled, tabIndex, minWidth, maxWidth, width, fullWidth, onClear, hideClear, mask, maskTokens, maskStrict, } = props;
    // The fill-in mask, when set. maskTokens changes identity on every render of most callers, so its content is the key.
    const tokensKey = JSON.stringify(maskTokens || {}, (_k, v) => (v instanceof RegExp ? v.source : v));
    const segments = React.useMemo(() => (mask ? parseMask(mask, maskTokens) : null), [mask, tokensKey]);
    const inputContainerRef = React.useRef(null);
    const inputRef = React.useRef(null);
    const prefixTriggerRef = React.useRef(null);
    const suffixTriggerRef = React.useRef(null);
    const [showPassword, setShowPassword] = React.useState(false);
    const [prefixDropOpen, setPrefixDropOpen] = React.useState(false);
    const [suffixDropOpen, setSuffixDropOpen] = React.useState(false);
    const isPasswordType = type === 'password';
    const actualInputType = isPasswordType && showPassword ? 'text' : type;
    // String values from dropdown addons — passed as prefix/suffix params in onChange
    const prefixStr = prefix && isAddonDropdown(prefix) ? ((_a = prefix.selected) !== null && _a !== void 0 ? _a : '') : '';
    const suffixStr = suffix && isAddonDropdown(suffix) ? ((_b = suffix.selected) !== null && _b !== void 0 ? _b : '') : '';
    // Whether each slot shows the secondary background + border separator
    const prefixHasSeparator = !!prefix && (isAddonDropdown(prefix) ||
        (isPrefixConfig(prefix) && !!prefix.showSeparator));
    const suffixHasSeparator = !!suffix && (isAddonDropdown(suffix) ||
        (isPrefixConfig(suffix) && !!suffix.showSeparator));
    // Password visibility toggle
    const passwordToggle = isPasswordType && (_jsx("button", { type: "button", className: "uxpcore_input__password-toggle", onClick: () => setShowPassword(v => !v), tabIndex: -1, disabled: disabled || readOnly, children: _jsx(Icon, { icon: ['fal', showPassword ? 'eye-slash' : 'eye'] }) }));
    // Clear button
    const clearButton = (!hideClear && (value === null || value === void 0 ? void 0 : value.length) > 0 && !disabled && !readOnly) ? (_jsx("button", { type: "button", className: "uxpcore_input__clear-btn", onClick: (e) => {
            var _a, _b, _c, _d;
            e.stopPropagation();
            onClear ? onClear() : onChange('', prefix && isAddonDropdown(prefix) ? ((_b = (_a = prefix.options[0]) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : undefined) : undefined, suffix && isAddonDropdown(suffix) ? ((_d = (_c = suffix.options[0]) === null || _c === void 0 ? void 0 : _c.value) !== null && _d !== void 0 ? _d : undefined) : undefined);
        }, tabIndex: -1, children: _jsx(Icon, { icon: ['fas', 'times'] }) })) : null;
    // Extract the renderable ReactNode from any InputPrefixSuffix value
    const resolveSlotNode = (slot) => {
        if (isPrefixConfig(slot))
            return slot.value;
        return slot;
    };
    React.useImperativeHandle(ref, () => ({
        focus: () => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); },
        getElement: () => inputRef.current,
    }));
    const handleChange = (e) => {
        if (readOnly)
            return;
        let next = e.target.value;
        if (segments) {
            const previous = value || '';
            if (maskStrict)
                next = strictValue(next, previous, segments);
            // Typing forward adds the fixed text that follows; deleting never puts it back.
            if (next.length > previous.length)
                next = fillFixed(next, segments);
        }
        onChange(next, prefixStr || undefined, suffixStr || undefined);
    };
    function handleFocus(_e) {
        var _a;
        if (readOnly)
            return;
        (_a = inputContainerRef.current) === null || _a === void 0 ? void 0 : _a.classList.add("uxpcore_input--focus");
        // An empty masked input starts with its leading fixed text, e.g. "B".
        if (segments && !value) {
            const start = fillFixed('', segments);
            if (start)
                onChange(start, prefixStr || undefined, suffixStr || undefined);
        }
        if (props.onFocus)
            props.onFocus();
    }
    function handleBlur(_e) {
        var _a;
        if (readOnly)
            return;
        (_a = inputContainerRef.current) === null || _a === void 0 ? void 0 : _a.classList.remove("uxpcore_input--focus");
        if (props.onBlur)
            props.onBlur(value);
    }
    function handleKeyDown(e) {
        if (readOnly)
            return;
        if (props.onKeyDown)
            props.onKeyDown(e, value);
    }
    const inputSizeStyles = getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style });
    const stateClass = getInputStateClass({ disabled, readOnly, isValid });
    const hasPrefixSlot = !!prefix;
    // hasSuffixSlot drives --has-suffix (reduces input right padding) and suffix div rendering
    const hasSuffixContent = !!suffix || isPasswordType;
    const hasSuffixSlot = hasSuffixContent || !!clearButton;
    const inputElement = (_jsx("input", Object.assign({ type: actualInputType, value: value || '', className: classNames("uxpcore_input__input", "uxp-form-input", { "uxpcore_input__input--active": value }), onChange: handleChange, onFocus: handleFocus, onBlur: handleBlur, onKeyDown: handleKeyDown }, inputAttr, { placeholder: segments ? "" : (placeholder || ""), disabled: disabled, readOnly: readOnly, ref: inputRef, tabIndex: tabIndex || 0, dir: context.direction || 'ltr' })));
    return (_jsxs("div", { className: classNames("uxpcore_input", "uxp-input-container", className, stateClass, {
            "uxpcore_input--has-prefix": hasPrefixSlot,
            "uxpcore_input--has-suffix": hasSuffixSlot,
            "uxpcore_input--has-content": (value === null || value === void 0 ? void 0 : value.length) > 0,
            "uxpcore_input--inline": inline,
            "uxpcore_input--full-width": fullWidth,
            "uxpcore_input--masked": !!segments,
        }), ref: inputContainerRef, style: inputSizeStyles, children: [prefix && (_jsx("div", { className: classNames("uxpcore_input__prefix", {
                    "uxpcore_input__prefix--separator": prefixHasSeparator,
                }), children: isAddonDropdown(prefix) ? (_jsxs(_Fragment, { children: [_jsxs("div", { ref: prefixTriggerRef, className: "uxpcore_input__addon-trigger", onClick: () => { if (!disabled && !readOnly)
                                setPrefixDropOpen(v => !v); }, children: [renderAddonIcon((_c = prefix.options.find(o => o.value === prefix.selected)) === null || _c === void 0 ? void 0 : _c.icon), _jsx("span", { children: (_g = (_f = (_d = prefix.options.find(o => o.value === prefix.selected)) === null || _d === void 0 ? void 0 : _d.label) !== null && _f !== void 0 ? _f : prefix.placeholder) !== null && _g !== void 0 ? _g : '—' }), _jsx(DropdownIndicator, { isOpen: prefixDropOpen })] }), _jsx(Dropdown, { externalTriggerRef: prefixTriggerRef, isOpen: prefixDropOpen, onToggle: () => setPrefixDropOpen(false), content: _jsx(AddonDropdownContent, { config: prefix, close: () => setPrefixDropOpen(false), onSelect: (v) => onChange(value, v, suffixStr || undefined) }), contentClassName: "uxpcore_select__dropdown", position: "bottom-left", minWidth: "8rem", maxWidth: "20rem" })] })) : isPrefixConfig(prefix)
                    ? _jsx("div", { className: "uxpcore_input__slot-content", children: prefix.value })
                    : resolveSlotNode(prefix) })), segments ? (_jsxs("div", { className: "uxpcore_input__masked", children: [_jsxs("span", { className: "uxpcore_input__mask", "aria-hidden": "true", children: [_jsx("span", { className: "uxpcore_input__mask-typed", children: value || '' }), maskRest(value || '', segments).map((part, i) => (_jsx("span", { className: part.blank ? "uxpcore_input__mask-blank" : "uxpcore_input__mask-fixed", children: part.text }, i)))] }), inputElement] })) : inputElement, clearButton, hasSuffixContent && (_jsx("div", { className: classNames("uxpcore_input__suffix", {
                    "uxpcore_input__suffix--separator": suffixHasSeparator,
                }), children: isAddonDropdown(suffix) ? (_jsxs(_Fragment, { children: [_jsxs("div", { ref: suffixTriggerRef, className: "uxpcore_input__addon-trigger", onClick: () => { if (!disabled && !readOnly)
                                setSuffixDropOpen(v => !v); }, children: [renderAddonIcon((_h = suffix.options.find(o => o.value === suffix.selected)) === null || _h === void 0 ? void 0 : _h.icon), _jsx("span", { children: (_m = (_l = (_j = suffix.options.find(o => o.value === suffix.selected)) === null || _j === void 0 ? void 0 : _j.label) !== null && _l !== void 0 ? _l : suffix.placeholder) !== null && _m !== void 0 ? _m : '—' }), _jsx(DropdownIndicator, { isOpen: suffixDropOpen })] }), _jsx(Dropdown, { externalTriggerRef: suffixTriggerRef, isOpen: suffixDropOpen, onToggle: () => setSuffixDropOpen(false), content: _jsx(AddonDropdownContent, { config: suffix, close: () => setSuffixDropOpen(false), onSelect: (v) => onChange(value, prefixStr || undefined, v) }), contentClassName: "uxpcore_select__dropdown", position: "bottom-right", minWidth: "8rem", maxWidth: "20rem" })] })) : (_jsx(_Fragment, { children: suffix
                        ? isPrefixConfig(suffix)
                            ? _jsx("div", { className: "uxpcore_input__slot-content", children: suffix.value })
                            : resolveSlotNode(suffix)
                        : passwordToggle })) }))] }));
});
Input.displayName = 'Input';
export default Input;
